import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type {
  AvalaraManagementCompany,
  CompanyNexusDetail,
  CompanyOverview,
  NexusSyncSelection,
  NexusSyncResult,
  SetDefaultTaxCodeResult,
} from '@/types/avalaraManagementCompany'
import type { AvalaraCompanySyncStatus } from '@/types/avalaraSync'
import type {
  CompanyEuRegistration,
  EuRegistrationNumberName,
  EuRegistrationParameterName,
} from '@/types/avalaraEuRegistration'
import {
  CLOCK_SKEW_TOLERANCE_MS,
  advanceCompanySyncTracking,
  parseInstant,
} from '@/util/companySyncStatus'
import type {
  CompanySyncOutcome,
  TrackedCompanySync,
} from '@/util/companySyncStatus'
import type { AuthState } from '@/types/authState'
import {
  avalaraManagementApi,
  companySyncBusyDetails,
} from '@/services/avalaraManagementService'
import type { GetCompaniesParams } from '@/services/avalaraManagementService'

export const useAvalaraManagementStore = defineStore('avalaraManagement', () => {
  const _companies = ref<AvalaraManagementCompany[]>([])
  const _isLoading = ref(false)
  const _error = ref<string | null>(null)
  const _hasFetched = ref(false)
  const _totalElements = ref(0)
  const _currentPage = ref(0)
  const _pageSize = ref(20)

  // Separate, unpaged cache for the bulk-align drawer. The main list paginates
  // server-side (20 rows/page); the drawer needs the whole set so its filter +
  // sort + select-all-visible all see the full dataset. Kept independent of
  // _companies so a drawer fetch doesn't disturb the main list's pagination state.
  const _allCompanies = ref<AvalaraManagementCompany[]>([])
  const _isLoadingAllCompanies = ref(false)

  // Detail view state — the full hierarchical nexus tree for a single company.
  const _companyNexusDetail = ref<CompanyNexusDetail | null>(null)
  const _isLoadingCompanyNexusDetail = ref(false)
  const _companyNexusDetailError = ref<string | null>(null)

  // Overview tab state — the single-round-trip aggregate for the Overview tab.
  const _companyOverview = ref<CompanyOverview | null>(null)
  const _isLoadingCompanyOverview = ref(false)
  // Sequencing token guards against a fast companyId switch letting a slower, stale
  // response overwrite a newer one in this shared singleton store.
  let _companyOverviewRequestId = 0

  // Company sync: the page tracks one run (see util/companySyncStatus). The status is
  // per backend pod, so a read does not always know the tracked run.
  const _trackedCompanySync = ref<TrackedCompanySync | null>(null)
  const _companySyncOutcome = ref<CompanySyncOutcome | null>(null)
  // Latest-wins token. A POST bumps it so a status read that began before the POST
  // cannot answer for the new run.
  let _companySyncStatusRequestId = 0

  const isLoaded = computed(() => _hasFetched.value)
  const isLoading = computed(() => _isLoading.value)
  const error = computed(() => _error.value)
  const totalElements = computed(() => _totalElements.value)
  const currentPage = computed(() => _currentPage.value)
  const pageSize = computed(() => _pageSize.value)

  const companyNexusDetail = computed(() => _companyNexusDetail.value)
  const isLoadingCompanyNexusDetail = computed(() => _isLoadingCompanyNexusDetail.value)
  const companyNexusDetailError = computed(() => _companyNexusDetailError.value)

  const companyOverview = computed(() => _companyOverview.value)
  const isLoadingCompanyOverview = computed(() => _isLoadingCompanyOverview.value)

  const fetchCompanies = async (
    authState: AuthState,
    params?: GetCompaniesParams,
  ) => {
    _isLoading.value = true
    _error.value = null
    try {
      const response = await avalaraManagementApi.getCompanies(authState, params)
      _companies.value = response.Content
      _totalElements.value = response.TotalElements
      _currentPage.value = response.Number
      _pageSize.value = response.Size
      _hasFetched.value = true
    } catch (err) {
      _error.value =
        err instanceof Error ? err.message : 'Failed to load companies'
      throw err
    } finally {
      _isLoading.value = false
    }
  }

  const fetchCompanyNexusDetail = async (
    authState: AuthState,
    companyId: number,
  ): Promise<CompanyNexusDetail> => {
    _isLoadingCompanyNexusDetail.value = true
    _companyNexusDetailError.value = null
    try {
      const detail = await avalaraManagementApi.getCompanyNexusDetail(authState, companyId)
      _companyNexusDetail.value = detail
      return detail
    } catch (err) {
      _companyNexusDetailError.value =
        err instanceof Error ? err.message : 'Failed to load company nexus detail'
      throw err
    } finally {
      _isLoadingCompanyNexusDetail.value = false
    }
  }

  const fetchCompanyOverview = async (
    authState: AuthState,
    companyId: number,
  ): Promise<CompanyOverview> => {
    const requestId = ++_companyOverviewRequestId
    _isLoadingCompanyOverview.value = true
    try {
      const overview = await avalaraManagementApi.getCompanyOverview(authState, companyId)
      // Latest-wins: ignore this response if a newer fetchCompanyOverview call has
      // started since this one began (fast company switch).
      if (requestId === _companyOverviewRequestId) {
        _companyOverview.value = overview
      }
      return overview
    } finally {
      if (requestId === _companyOverviewRequestId) {
        _isLoadingCompanyOverview.value = false
      }
    }
  }

  const clearCompanyOverview = () => {
    _companyOverview.value = null
    // Bump the request id so an in-flight fetchCompanyOverview started before this
    // clear can't repopulate _companyOverview after the clear settles.
    ++_companyOverviewRequestId
  }

  // Fetch ALL companies (unpaged) for the bulk-align drawer. Caller passes a
  // generous `size` cap; backend currently has ~100 companies in prod, so 5000
  // gives multi-x headroom while still bounding worst-case payload size.
  const fetchAllCompanies = async (
    authState: AuthState,
    size = 5000,
  ): Promise<void> => {
    _isLoadingAllCompanies.value = true
    try {
      const response = await avalaraManagementApi.getCompanies(authState, { page: 0, size })
      _allCompanies.value = response.Content
    } finally {
      _isLoadingAllCompanies.value = false
    }
  }

  const clearCompanyNexusDetail = () => {
    _companyNexusDetail.value = null
    _companyNexusDetailError.value = null
  }

  const syncNexus = async (
    authState: AuthState,
    companyId: number,
    templateType: 'digital' | 'physical',
    selection: NexusSyncSelection,
  ): Promise<NexusSyncResult> => {
    return avalaraManagementApi.syncNexus(authState, companyId, templateType, selection)
  }

  // The outcome of the previous run must not stay visible while a new run is tracked.
  const beginCompanySyncTracking = (tracked: TrackedCompanySync) => {
    _trackedCompanySync.value = tracked
    _companySyncOutcome.value = null
  }

  // Each outcome is a new object, so a watcher fires for two outcomes of the same kind.
  const applyCompanySyncRead = (status: AvalaraCompanySyncStatus | null) => {
    const { tracked, outcome } = advanceCompanySyncTracking(
      _trackedCompanySync.value,
      status,
      Date.now(),
    )
    if (_trackedCompanySync.value === null && tracked !== null) {
      beginCompanySyncTracking(tracked)
      return
    }
    _trackedCompanySync.value = tracked
    if (outcome) _companySyncOutcome.value = outcome
  }

  const invalidateInFlightStatusReads = () => {
    ++_companySyncStatusRequestId
  }

  // Never throws: a failed read is "no evidence", and the poll retries.
  const fetchCompanySyncStatus = async (authState: AuthState): Promise<void> => {
    const requestId = ++_companySyncStatusRequestId
    try {
      const status = await avalaraManagementApi.getCompanySyncStatus(authState)
      if (requestId !== _companySyncStatusRequestId) return
      applyCompanySyncRead(status)
    } catch {
      if (requestId !== _companySyncStatusRequestId) return
      applyCompanySyncRead(null)
    }
  }

  const syncCompanies = async (authState: AuthState): Promise<void> => {
    const postedAtMs = Date.now()
    try {
      await avalaraManagementApi.syncCompanies(authState)
    } catch (err) {
      const busy = companySyncBusyDetails(err)
      if (busy) {
        invalidateInFlightStatusReads()
        beginCompanySyncTracking({
          anchorMs: parseInstant(busy.startedAt) ?? postedAtMs - CLOCK_SKEW_TOLERANCE_MS,
          lastEvidenceMs: Date.now(),
          startedAt: busy.startedAt,
          requestedBy: busy.requestedBy,
          source: busy.source,
        })
      }
      throw err
    }
    invalidateInFlightStatusReads()
    beginCompanySyncTracking({
      anchorMs: postedAtMs - CLOCK_SKEW_TOLERANCE_MS,
      lastEvidenceMs: Date.now(),
      startedAt: new Date(postedAtMs).toISOString(),
      source: 'ADHOC',
    })
    await fetchCompanySyncStatus(authState)
  }

  /**
   * Sets or clears a company's default Avalara tax code.
   * On success, updates the cached companyOverview's defaultTaxCode in place (if the
   * overview for this company is currently loaded) so the UI reflects the change
   * immediately without waiting for a full overview refetch. Throws on error.
   */
  const setDefaultTaxCode = async (
    authState: AuthState,
    companyId: number,
    taxCode: string | null,
  ): Promise<SetDefaultTaxCodeResult> => {
    const result = await avalaraManagementApi.setDefaultTaxCode(authState, companyId, taxCode)
    if (_companyOverview.value && _companyOverview.value.id === companyId) {
      _companyOverview.value = {
        ..._companyOverview.value,
        defaultTaxCode: result.defaultTaxCode,
      }
    }
    return result
  }

  const fetchEuRegistration = (
    authState: AuthState,
    companyId: number,
  ): Promise<CompanyEuRegistration> => {
    return avalaraManagementApi.getEuRegistration(authState, companyId)
  }

  /** PUT with no value: adds an unset number or resets a set one to the Reach value. */
  const applyReachEuRegistrationValue = (
    authState: AuthState,
    companyId: number,
    name: EuRegistrationNumberName,
  ): Promise<void> => {
    return avalaraManagementApi.putEuRegistrationParameter(authState, companyId, name)
  }

  const setIntermediaryAppointed = (
    authState: AuthState,
    companyId: number,
    value: boolean,
  ): Promise<void> => {
    return avalaraManagementApi.putEuRegistrationParameter(
      authState,
      companyId,
      'IsIntermediaryAppointed',
      value,
    )
  }

  const removeEuRegistrationParameter = (
    authState: AuthState,
    companyId: number,
    name: EuRegistrationParameterName,
  ): Promise<void> => {
    return avalaraManagementApi.deleteEuRegistrationParameter(authState, companyId, name)
  }

  return {
    companies: _companies,
    allCompanies: _allCompanies,
    isLoading,
    isLoadingAllCompanies: computed(() => _isLoadingAllCompanies.value),
    isLoaded,
    error,
    totalElements,
    currentPage,
    pageSize,

    fetchAllCompanies,

    companyNexusDetail,
    isLoadingCompanyNexusDetail,
    companyNexusDetailError,

    companyOverview,
    isLoadingCompanyOverview,

    fetchCompanies,
    fetchCompanyNexusDetail,
    clearCompanyNexusDetail,
    fetchCompanyOverview,
    clearCompanyOverview,
    setDefaultTaxCode,
    fetchEuRegistration,
    applyReachEuRegistrationValue,
    setIntermediaryAppointed,
    removeEuRegistrationParameter,
    syncNexus,
    syncCompanies,
    fetchCompanySyncStatus,
    trackedCompanySync: computed(() => _trackedCompanySync.value),
    isCompanySyncRunning: computed(() => _trackedCompanySync.value !== null),
    companySyncOutcome: computed(() => _companySyncOutcome.value),
  }
})
